import { LitElement, html, css, nothing, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { noSelectionOption, valueForSelect, valueFromSelect } from '../utils/select-sentinel.js';

/** One saved value whose alias no longer names a field in the schema. */
export interface OrphanedInput {
    alias: string;
    value: unknown;
}

/** A field the author can move an orphaned value into — one that has no value today. */
export interface OrphanTarget {
    alias: string;
    label: string;
}

/**
 * What the author sees of a value the schema no longer has a field for: the alias, the value, and
 * the two ways out — move it into a field that is still empty, or throw it away.
 *
 * <p>It is an UPPLYSNING, not a fault: nothing is broken, the author renamed or removed a field and
 * the answer that belonged to the old name is still here. So no red, no green — a glyph and the
 * sentence carry it, which is also the only form that survives without colour vision.</p>
 *
 * <p>Owns no storage and no schema: the host hands it rows and targets and receives
 * `orphan-move` / `orphan-discard`. The host is the one place that knows what a move MEANS (it goes
 * through the same input path a typed value takes, so the evaluation follows).</p>
 */
@customElement('ixit-orphaned-inputs-box')
export class IxitOrphanedInputsBox extends LitElement {
    static styles: CSSResultGroup = css`
        :host {
            display: block;
        }
        .box {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin: 0 0 12px;
            padding: 10px 12px;
            border: 1px solid var(--uui-color-border);
            border-radius: var(--uui-border-radius, 4px);
            background: var(--uui-color-surface);
            color: var(--uui-color-text);
            font-size: 0.85rem;
        }
        .headline {
            display: flex;
            align-items: baseline;
            gap: 8px;
            font-weight: 600;
        }
        .row {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
        }
        .pair {
            flex: 1 1 220px;
            min-width: 0;
            display: flex;
            align-items: baseline;
            gap: 6px;
        }
        .alias {
            font-family: var(--uui-font-monospace, monospace);
        }
        .value {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            /* An alpha of the theme's own text colour, so it composites over whatever ground the
               theme puts behind it — a fixed grey does not have that property. */
            color: color-mix(in srgb, var(--uui-color-text) 70%, transparent);
        }
        uui-select {
            flex: 0 1 200px;
        }
    `;

    @property({ attribute: false }) entries: OrphanedInput[] = [];

    @property({ attribute: false }) targets: OrphanTarget[] = [];

    render() {
        if (this.entries.length === 0) return nothing;

        const headline = this.entries.length === 1
            ? 'Ett sparat värde hör inte längre till något fält:'
            : `${this.entries.length} sparade värden hör inte längre till något fält:`;

        return html`
            <div class="box" role="status">
                <div class="headline">
                    <span aria-hidden="true">ℹ</span>
                    <span>${headline}</span>
                </div>
                ${this.entries.map((entry) => this._renderRow(entry))}
            </div>
        `;
    }

    private _renderRow(entry: OrphanedInput) {
        const shown = displayValue(entry.value);
        return html`
            <div class="row">
                <span class="pair">
                    <span class="alias">${entry.alias}</span>
                    <span class="value" title=${shown}>= ${shown}</span>
                </span>
                <uui-select
                    label="Flytta ${entry.alias} till ett fält"
                    ?disabled=${this.targets.length === 0}
                    .options=${[
                        noSelectionOption(
                            this.targets.length === 0 ? 'Inga tomma fält' : 'Flytta till…', true),
                        ...this.targets.map((t) => ({
                            value: t.alias,
                            name: t.label === t.alias ? t.alias : `${t.label} (${t.alias})`,
                            selected: false,
                        })),
                    ]}
                    .value=${valueForSelect('')}
                    @change=${(e: any) => this._move(entry.alias, valueFromSelect(e.target.value))}
                ></uui-select>
                <uui-button
                    look="default"
                    compact
                    label="Släng ${entry.alias}"
                    @click=${() => this._discard(entry.alias)}
                >Släng</uui-button>
            </div>
        `;
    }

    private _move(alias: string, target: string) {
        if (!target) return;
        this.dispatchEvent(new CustomEvent('orphan-move', {
            detail: { alias, target }, bubbles: true, composed: true,
        }));
    }

    private _discard(alias: string) {
        this.dispatchEvent(new CustomEvent('orphan-discard', {
            detail: { alias }, bubbles: true, composed: true,
        }));
    }
}

/** The value as one line of text. A plugin item's leftover state is a structure, and the author
 *  still has to recognise it, so it is shown as its JSON rather than as `[object Object]`. */
function displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '(tomt)';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

declare global {
    interface HTMLElementTagNameMap {
        'ixit-orphaned-inputs-box': IxitOrphanedInputsBox;
    }
}
